import { useContext } from 'react';
import isEqual from 'react-fast-compare';
import { useSyncExternalStoreWithSelector } from 'use-sync-external-store/shim/with-selector';

import { GraphExplorerContext } from 'dashboard/components/GraphModule/GraphCanvasContextProvider';
import { GraphExplorer } from 'dashboard/models/graph-explorer';

// In order for this hook to function, it must be preceded by a GraphContextWrapper
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function useGraphExplorer(graphExplorerRef?: React.MutableRefObject<GraphExplorer> | null) {
  const graphExplorer = useContext(GraphExplorerContext);

  if (graphExplorerRef) {
    graphExplorerRef.current = graphExplorer;
  }

  const selectedNodes = useSyncExternalStoreWithSelector(
    graphExplorer.reactSubscribe,
    graphExplorer.getSnapshot,
    undefined,
    (snapshot) => snapshot.selectedNodes,
    isEqual,
  );

  const exportNodes = useSyncExternalStoreWithSelector(
    graphExplorer.reactSubscribe,
    graphExplorer.getSnapshot,
    undefined,
    (snapshot) => snapshot.exportNodes,
    isEqual,
  );

  const hiddenNodes = useSyncExternalStoreWithSelector(
    graphExplorer.reactSubscribe,
    graphExplorer.getSnapshot,
    undefined,
    (snapshot) => snapshot.hiddenNodes,
    isEqual,
  );

  const visibleNodes = useSyncExternalStoreWithSelector(
    graphExplorer.reactSubscribe,
    graphExplorer.getSnapshot,
    undefined,
    (snapshot) => snapshot.visibleNodes,
    isEqual,
  );

  const seedNodes = useSyncExternalStoreWithSelector(
    graphExplorer.reactSubscribe,
    graphExplorer.getSnapshot,
    undefined,
    (snapshot) => snapshot.seedNodes,
    isEqual,
  );

  const excludedNodes = useSyncExternalStoreWithSelector(
    graphExplorer.reactSubscribe,
    graphExplorer.getSnapshot,
    undefined,
    (snapshot) => snapshot.excludedNodes,
    isEqual,
  );

  const zoomPercentage = useSyncExternalStoreWithSelector(
    graphExplorer.reactSubscribe,
    graphExplorer.getSnapshot,
    undefined,
    (snapshot) => snapshot.zoomPercentage,
  );

  const canZoomIn = useSyncExternalStoreWithSelector(
    graphExplorer.reactSubscribe,
    graphExplorer.getSnapshot,
    undefined,
    (snapshot) => snapshot.canZoomIn,
  );

  const canZoomOut = useSyncExternalStoreWithSelector(
    graphExplorer.reactSubscribe,
    graphExplorer.getSnapshot,
    undefined,
    (snapshot) => snapshot.canZoomOut,
  );

  const excludedNodesVisible = useSyncExternalStoreWithSelector(
    graphExplorer.reactSubscribe,
    graphExplorer.getSnapshot,
    undefined,
    (snapshot) => snapshot.excludedNodesVisible,
  );

  const selectedViewType = useSyncExternalStoreWithSelector(
    graphExplorer.reactSubscribe,
    graphExplorer.getSnapshot,
    undefined,
    (snapshot) => snapshot.selectedViewType,
  );

  return {
    graphExplorer,
    selectedNodes,
    hiddenNodes,
    visibleNodes,
    seedNodes,
    excludedNodes,
    excludedNodesVisible,
    exportNodes,
    zoomPercentage,
    canZoomIn,
    canZoomOut,
    selectedViewType,
  };
}
